'use client';

import { useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
} from '@lexical/markdown';
import { AlertCircle, Check, FileText, Loader2, Send } from 'lucide-react';

import { useMyDosespotClinicianInfoQuery } from '@/entities/dosespot-clinician';
import { useMyEmployeeRecordQuery } from '@/entities/me';
import { priorAuthKeys, usePriorAuthDetailsQuery } from '@/entities/prior-auth';
import { logger } from '@/shared';
import { LexicalMarkdownEditor } from '@/shared/ui/primitives/lexical-markdown-editor';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';

import {
  checkLmnAttachAvailability,
  extractLmnErrorDetail,
  signAndAttachLmn,
  type LmnCaseDraftT,
  type PriorAuthCommandResponseT,
} from '../api/lmnApi';

const LMN_MARKDOWN_TRANSFORMERS = [
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
] as const;
const CONFIRMATION_POLL_INTERVAL_MS = 5_000;

type AttachStatusT =
  | { kind: 'pending'; message: string; startedAt: number }
  | { kind: 'success' | 'error'; message: string };

interface LmnDraftReviewProps {
  draft: LmnCaseDraftT;
  patientUserId: string;
  markdown: string;
  onMarkdownChange: (markdown: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAttached?: (result: PriorAuthCommandResponseT) => void;
}

export function LmnDraftReview({
  draft,
  patientUserId,
  markdown,
  onMarkdownChange,
  open,
  onOpenChange,
  onAttached,
}: LmnDraftReviewProps) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [attachStatus, setAttachStatus] = useState<AttachStatusT | null>(null);

  const expectedFileName = `lmn-pa-${draft.prior_auth_record_id}.pdf`.toLowerCase();
  const detailsQuery = usePriorAuthDetailsQuery(draft.prior_auth_record_id, {
    refetchInterval: ({ state }) => {
      if (attachStatus?.kind !== 'pending') {
        return false;
      }
      if (state.dataUpdatedAt <= attachStatus.startedAt) {
        return CONFIRMATION_POLL_INTERVAL_MS;
      }

      const attachments = state.data?.dosespotCase?.case.Attachments ?? [];
      const letterWasAdded = attachments.some(
        (attachment) => (attachment.FileName ?? '').toLowerCase() === expectedFileName
      );
      const canRetry = state.data?.allowedActions.includes('UPLOAD_ATTACHMENT') === true;
      return letterWasAdded || canRetry ? false : CONFIRMATION_POLL_INTERVAL_MS;
    },
  });

  const availability = useQuery({
    queryKey: ['lmnAttachAvailability', draft.prior_auth_record_id],
    queryFn: ({ signal }) =>
      checkLmnAttachAvailability(
        {
          patient_user_id: patientUserId,
          prior_auth_record_id: draft.prior_auth_record_id,
        },
        signal
      ),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const employeeRecordQuery = useMyEmployeeRecordQuery(open);
  const dosespotClinicianQuery = useMyDosespotClinicianInfoQuery(open);
  const dosespotClinician = dosespotClinicianQuery.data;
  const dosespotClinicianName = dosespotClinician
    ? [
        dosespotClinician.Prefix,
        dosespotClinician.FirstName,
        dosespotClinician.MiddleName,
        dosespotClinician.LastName,
        dosespotClinician.Suffix,
      ]
        .filter(Boolean)
        .join(' ')
        .trim()
    : null;
  const signatureCaption = employeeRecordQuery.data?.displayName?.trim() || dosespotClinicianName;
  const signerNpi =
    employeeRecordQuery.data?.npiNumber?.trim() || dosespotClinician?.NPINumber?.trim() || null;
  const signerIdentityReady =
    employeeRecordQuery.data !== undefined &&
    !employeeRecordQuery.isError &&
    dosespotClinician != null &&
    !dosespotClinicianQuery.isError &&
    signatureCaption !== null;
  const signerIdentityUnavailable =
    employeeRecordQuery.isError ||
    dosespotClinicianQuery.isError ||
    (!dosespotClinicianQuery.isLoading && dosespotClinician === null);
  const signingDate = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (!open || dosespotClinician !== null || employeeRecordQuery.data === undefined) {
      return;
    }

    logger.error(
      'priorAuthLmn.signerIdentity.unavailable',
      {
        reason: 'dosespot-clinician-not-found',
        patientUserId,
        priorAuthRecordId: draft.prior_auth_record_id,
        employeeDisplayNameAvailable: Boolean(employeeRecordQuery.data?.displayName?.trim()),
        employeeNpiAvailable: Boolean(employeeRecordQuery.data?.npiNumber?.trim()),
      },
      new Error('DoseSpot clinician identity not found')
    );
  }, [
    dosespotClinician,
    draft.prior_auth_record_id,
    employeeRecordQuery.data,
    open,
    patientUserId,
  ]);

  const attach = useMutation({
    mutationFn: () =>
      signAndAttachLmn({
        patient_user_id: patientUserId,
        prior_auth_record_id: draft.prior_auth_record_id,
        markdown,
      }),
    onMutate: () => setAttachStatus(null),
    onSuccess: (result) => {
      if (result.outcome === 'REJECTED') {
        return;
      }

      if (result.outcome === 'CONFIRMATION_PENDING') {
        setAttachStatus({
          kind: 'pending',
          message: 'The letter is being added. This may take a moment.',
          startedAt: Date.now(),
        });
      } else {
        setAttachStatus({
          kind: 'success',
          message: 'The signed letter was added to this prior authorization.',
        });
        onAttached?.(result);
        onOpenChange(false);
      }
      void queryClient.invalidateQueries({ queryKey: priorAuthKeys.all });
      void queryClient.invalidateQueries({
        queryKey: ['lmnAttachAvailability', draft.prior_auth_record_id],
      });
      setConfirmOpen(false);
    },
  });

  const attachments = detailsQuery.data?.dosespotCase?.case.Attachments ?? [];
  const letterWasAdded = attachments.some(
    (attachment) => (attachment.FileName ?? '').toLowerCase() === expectedFileName
  );
  const confirmationWasRefreshed =
    attachStatus?.kind === 'pending' && detailsQuery.dataUpdatedAt > attachStatus.startedAt;
  const canRetryAttachment =
    confirmationWasRefreshed &&
    detailsQuery.data?.allowedActions.includes('UPLOAD_ATTACHMENT') === true;
  const displayedAttachStatus: AttachStatusT | null =
    confirmationWasRefreshed && letterWasAdded
      ? {
          kind: 'success',
          message: 'The signed letter was added to this prior authorization.',
        }
      : canRetryAttachment
        ? { kind: 'error', message: 'The letter could not be added. Please try again.' }
        : attachStatus;

  const blockedReason = canRetryAttachment
    ? null
    : availability.isError
      ? extractLmnErrorDetail(availability.error)
      : availability.data?.would_accept === false
        ? availability.data.message
        : null;
  const attachError = attach.isError
    ? extractLmnErrorDetail(attach.error)
    : attach.data?.outcome === 'REJECTED'
      ? (attach.data.rejectionDetail ??
        attach.data.rejectionCode ??
        'DoseSpot did not accept the letter.')
      : null;
  const isEmpty = markdown.trim().length === 0;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="grid h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:max-w-[min(90rem,calc(100vw-2rem))]">
          <DialogHeader className="border-b px-6 py-5 pr-14">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <FileText className="h-5 w-5 text-muted-foreground" aria-hidden />
              Review supporting letter
            </DialogTitle>
            <DialogDescription>
              Edit the letter, resolve anything that needs verification, then save or sign it.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 overflow-y-auto bg-muted/40 p-4 sm:p-6">
            <div className="mx-auto grid w-full max-w-[74rem] items-start gap-6 lg:grid-cols-[minmax(0,51rem)_18rem]">
              <div className="min-w-0 pt-10">
                <div className="lmn-letter-page">
                  <LexicalMarkdownEditor
                    value={markdown}
                    onChange={onMarkdownChange}
                    toolbar="emphasis"
                    toolbarClassName="absolute -top-11 left-0 m-0 w-auto rounded-lg border bg-background p-1 text-muted-foreground shadow-[0_2px_8px_rgb(0_0_0_/_12%)]"
                    transformers={LMN_MARKDOWN_TRANSFORMERS}
                    className="lmn-letter-editor"
                  />

                  <div className="lmn-letter-signature" aria-label="Signature preview">
                    {signerIdentityReady ? (
                      <>
                        <p>Electronically signed by {signatureCaption}</p>
                        {signerNpi ? <p>NPI: {signerNpi}</p> : null}
                        <p>Signed {signingDate}</p>
                      </>
                    ) : (
                      <p className="italic">
                        {signerIdentityUnavailable
                          ? 'Signer details are unavailable. Confirm your clinician profile before signing.'
                          : 'Loading signer details…'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <aside className="space-y-4 lg:sticky lg:top-0">
                <div className="rounded-xl border bg-background p-4">
                  <p className="text-sm font-medium">Before signing</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    Confirm the clinical facts and remove any placeholder language. The page at left
                    matches the electronically signed PDF.
                  </p>
                </div>

                {draft.flagged_gaps.length > 0 ? (
                  <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600" aria-hidden />
                      <p className="text-sm font-medium">
                        {draft.flagged_gaps.length}{' '}
                        {draft.flagged_gaps.length === 1 ? 'item' : 'items'} to review
                      </p>
                    </div>
                    <ul className="mt-3 list-disc space-y-2 pl-4 text-xs leading-relaxed text-muted-foreground">
                      {draft.flagged_gaps.map((gap) => (
                        <li key={gap}>{gap}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="flex gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm">
                    <Check className="mt-0.5 h-4 w-4 flex-none text-emerald-600" aria-hidden />
                    <span>No missing information was identified.</span>
                  </div>
                )}
              </aside>
            </div>
          </div>

          <DialogFooter className="items-center border-t bg-background px-6 py-4 sm:justify-between">
            <div className="min-w-0 flex-1">
              {blockedReason ? <p className="text-xs text-destructive">{blockedReason}</p> : null}
              {displayedAttachStatus ? (
                <div
                  className={`flex items-start gap-2 text-xs ${
                    displayedAttachStatus.kind === 'error'
                      ? 'text-destructive'
                      : 'text-muted-foreground'
                  }`}
                >
                  {displayedAttachStatus.kind === 'pending' ? (
                    <Loader2 className="mt-0.5 h-4 w-4 flex-none animate-spin" aria-hidden />
                  ) : displayedAttachStatus.kind === 'error' ? (
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-none" aria-hidden />
                  ) : (
                    <Check className="mt-0.5 h-4 w-4 flex-none text-emerald-600" aria-hidden />
                  )}
                  <span>{displayedAttachStatus.message}</span>
                </div>
              ) : null}
            </div>
            <div className="flex flex-none gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Save
              </Button>
              <Button
                onClick={() => setConfirmOpen(true)}
                disabled={
                  isEmpty ||
                  availability.isLoading ||
                  blockedReason !== null ||
                  !signerIdentityReady ||
                  displayedAttachStatus?.kind === 'pending' ||
                  displayedAttachStatus?.kind === 'success'
                }
              >
                <Send className="mr-1 h-4 w-4" aria-hidden />
                Sign and add letter
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sign and add this letter?</DialogTitle>
            <DialogDescription>
              Cortex will record your authenticated approval and add the electronically signed PDF
              to this prior authorization.
            </DialogDescription>
          </DialogHeader>

          {signerIdentityReady ? (
            <div className="my-3 rounded-md border bg-muted/30 p-3 text-sm">
              <p>Electronically signed by {signatureCaption}</p>
              {signerNpi ? <p>NPI: {signerNpi}</p> : null}
              <p>Signed {signingDate}</p>
            </div>
          ) : null}

          {attachError ? (
            <p className="text-sm text-destructive" role="alert">
              {attachError}
            </p>
          ) : null}

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={attach.isPending}
            >
              Go back
            </Button>
            <Button
              onClick={() => attach.mutate()}
              disabled={attach.isPending || !signerIdentityReady}
            >
              {attach.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Send className="mr-1 h-4 w-4" aria-hidden />
              )}
              Sign and add letter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
